/**
 * TradeMenu — trade-hub planet. Ribbons:
 *   • Exchange  — buy Universal Units with minerals (100 : 1).
 *   • Market    — rotating shop (re-rolls every 5 min) selling items for Units.
 *   • Sell      — sell modifications / items for Units.
 *   • Build Mods— spend Units to randomise a shield generator (rarity-based).
 */
import { useState } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { TRADE, MODS, ITEM_SELL } from "@/config/balance";
import {
  RARITY_META,
  modName,
  type Modification,
  type Rarity,
} from "@/game/mods/modifications";
import { itemIcon } from "./itemIcons";

interface Props {
  snap: HudSnapshot;
  onTrade: (units: number) => void;
  onBuyMarket: (id: string) => void;
  onBuildMod: () => Modification | null;
  onSellMod: (id: string) => void;
  onSellItem: (id: string) => void;
  onClose: () => void;
}

type Tab = "exchange" | "market" | "sell" | "build";
const AMOUNTS = [1, 10, 50, 100, 500, 1000];
const SELL_ITEMS: { id: string; name: string }[] = [
  { id: "moonCore", name: "Moon Core" },
  { id: "energyCore", name: "Energy Core" },
  { id: "darkMatter", name: "Dark Matter" },
  { id: "warpFuel", name: "Warp Drive Fuel" },
];
const ODDS: { r: Rarity; pct: number }[] = [
  { r: "common", pct: 40 },
  { r: "rare", pct: 35 },
  { r: "epic", pct: 15 },
  { r: "legendary", pct: 10 },
];

export default function TradeMenu({
  snap,
  onTrade,
  onBuyMarket,
  onBuildMod,
  onSellMod,
  onSellItem,
  onClose,
}: Props) {
  const [tab, setTab] = useState<Tab>("exchange");
  const [built, setBuilt] = useState<Modification | null>(null);
  const rate = TRADE.materialPerUnit;
  const mins = Math.max(1, Math.ceil(snap.marketRefreshIn / 60));
  const canBuild = snap.units >= snap.buildModCost;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Trade hub"
      >
        <div className="upg-head">
          <div className="upg-head-title">🛒 Trade Hub</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="trade-tabs">
          <button className={tab === "exchange" ? "on" : ""} onClick={() => setTab("exchange")}>
            Exchange
          </button>
          <button className={tab === "market" ? "on" : ""} onClick={() => setTab("market")}>
            Market
          </button>
          <button className={tab === "sell" ? "on" : ""} onClick={() => setTab("sell")}>
            Sell
          </button>
          <button className={tab === "build" ? "on" : ""} onClick={() => setTab("build")}>
            Build Mods
          </button>
        </div>

        <div className="stats-body">
          {tab === "exchange" && (
            <>
              <div className="svc-line">
                Exchange rate: <b>{rate} minerals = 1 Universal Unit</b>
              </div>
              <div className="stat-row">
                <span className="k">Minerals</span>
                <span className="v">{fmt(snap.minerals)}</span>
              </div>
              <div className="stat-row">
                <span className="k">Universal Units</span>
                <span className="v hi">{fmt(snap.units)}</span>
              </div>
              <div className="svc-actions">
                {AMOUNTS.map((n) => {
                  const cost = n * rate;
                  return (
                    <button
                      key={n}
                      className="hud-btn accent"
                      disabled={snap.minerals < cost}
                      onClick={() => onTrade(n)}
                    >
                      Buy {n} U · {fmt(cost)}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {tab === "market" && (
            <>
              <div className="svc-line">
                Restocks in ~{mins} min · pay with ◆ Units ({fmt(snap.units)})
              </div>
              {snap.market.length === 0 && (
                <div className="inv-empty">
                  Market is empty this cycle — check back later.
                </div>
              )}
              {snap.market.map((m) => {
                const ok = m.stock > 0 && snap.units >= m.cost;
                return (
                  <div key={m.id} className="market-row">
                    {itemIcon(m.id) ? (
                      <img className="market-img" src={itemIcon(m.id)} alt="" />
                    ) : (
                      <span className="market-icon">{m.icon}</span>
                    )}
                    <div className="market-info">
                      <div className="market-name">{m.name}</div>
                      <div className="market-meta">
                        ◆ {m.cost} U · stock {m.stock}
                      </div>
                    </div>
                    <button className="hud-btn accent" disabled={!ok} onClick={() => onBuyMarket(m.id)}>
                      Buy
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {tab === "sell" && (
            <>
              <div className="svc-line">Sell for ◆ Units ({fmt(snap.units)})</div>
              {snap.mods.map((m) => {
                const val = Math.max(1, Math.round(m.shieldBonus * MODS.sellFraction));
                return (
                  <div key={m.id} className="market-row">
                    <span className="market-icon" style={{ color: RARITY_META[m.rarity].color }}>
                      🛡
                    </span>
                    <div className="market-info">
                      <div className="market-name">{modName(m)}</div>
                      <div className="market-meta">+{m.shieldBonus} shield · sells for ◆ {val}</div>
                    </div>
                    <button className="hud-btn" onClick={() => onSellMod(m.id)}>
                      Sell
                    </button>
                  </div>
                );
              })}
              {SELL_ITEMS.map((it) => {
                const n = snap.inventory[it.id] ?? 0;
                const val = ITEM_SELL[it.id] ?? 0;
                if (n <= 0 || val <= 0) return null;
                return (
                  <div key={it.id} className="market-row">
                    {itemIcon(it.id) ? (
                      <img className="market-img" src={itemIcon(it.id)} alt="" />
                    ) : (
                      <span className="market-icon">◆</span>
                    )}
                    <div className="market-info">
                      <div className="market-name">
                        {it.name} <span className="inv-count">×{n}</span>
                      </div>
                      <div className="market-meta">sells for ◆ {val} each</div>
                    </div>
                    <button className="hud-btn" onClick={() => onSellItem(it.id)}>
                      Sell
                    </button>
                  </div>
                );
              })}
              {snap.mods.length === 0 &&
                SELL_ITEMS.every((it) => (snap.inventory[it.id] ?? 0) <= 0) && (
                  <div className="inv-empty">Nothing to sell right now.</div>
                )}
            </>
          )}

          {tab === "build" && (
            <>
              <div className="svc-line">
                Trade-hub tier <b>{snap.modTier}</b> · builds a random{" "}
                <b>Shield Generator</b> for ◆ {snap.buildModCost} U (◆ {fmt(snap.units)} U)
              </div>
              <div className="build-odds">
                {ODDS.map((o) => (
                  <span key={o.r} className="odds-chip" style={{ color: RARITY_META[o.r].color }}>
                    {RARITY_META[o.r].label} {o.pct}%
                  </span>
                ))}
              </div>
              <button
                className="hud-btn accent"
                disabled={!canBuild}
                onClick={() => setBuilt(onBuildMod())}
              >
                Build Shield Generator · ◆ {snap.buildModCost} U
              </button>
              {built && (
                <div
                  className="build-result"
                  style={{ borderColor: RARITY_META[built.rarity].color }}
                >
                  Built:{" "}
                  <b style={{ color: RARITY_META[built.rarity].color }}>
                    {RARITY_META[built.rarity].label}
                  </b>{" "}
                  Shield Generator · <b>+{built.shieldBonus} shield</b>. Equip it at a
                  🔧 mechanical yard.
                </div>
              )}
              <div className="build-note">
                Higher rarity and higher trade-hub tier give a bigger shield bonus.
                The tier rises every {MODS.tierEveryWarps} warp jumps.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}
